import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { auditLogsService } from 'components/api/auditLogs';
import { AuditLog, AuditLogsParams, AuditLogsResponse } from 'types/auditLog';
import dayjs from 'dayjs';

interface AuditLogsState {
  items: AuditLog[];
  total: number;
  isLoading: boolean;
  error: string | null;
  params: AuditLogsParams;
}

const now = dayjs();
const from = now.startOf('day');
const to = from.add(24, 'hour');

const initialState: AuditLogsState = {
  items: [],
  total: 0,
  isLoading: false,
  error: null,
  params: {
    page: 1,
    limit: 10,
    from: from.toISOString(),
    to: to.toISOString(),
  },
};

export const fetchAuditLogs = createAsyncThunk(
  'auditLogs/fetchAuditLogs',
  async (params: AuditLogsParams, { rejectWithValue }) => {
    try {
      const response = await auditLogsService.getAuditLogs(params);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Failed to fetch audit logs',
      );
    }
  },
);

const auditLogsSlice = createSlice({
  name: 'auditLogs',
  initialState,
  reducers: {
    setParams: (state, action: PayloadAction<Partial<AuditLogsParams>>) => {
      // If setting from/to, Clear hours shortcut
      if (action.payload.from || action.payload.to) {
        state.params = { ...state.params, ...action.payload, hours: undefined };
      } else if (action.payload.hours) {
        // If setting hours, clear date range
        state.params = { ...state.params, ...action.payload, from: undefined, to: undefined };
      } else {
        state.params = { ...state.params, ...action.payload };
      }
    },
    clearFilters: (state) => {
      state.params = { ...initialState.params };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action: PayloadAction<AuditLogsResponse>) => {
        state.isLoading = false;
        state.items = action.payload.data?.items || [];
        state.total = action.payload.data?.total || 0;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setParams, clearFilters } = auditLogsSlice.actions;
export default auditLogsSlice.reducer;
